import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';
import { Modal, Table, Spinner, FormControl, ProgressBar } from 'react-bootstrap';

/* ──────────────────────────────────────────────────────────────
   DESIGN TOKENS
────────────────────────────────────────────────────────────── */
const T = {
  navH   : 50,
  radius : 10,
  border : '#e2e8f0',
  muted  : '#94a3b8',
  navy   : '#0f172a',
  blue   : '#1e3a5f',
  sky    : '#0ea5e9',
  teal   : '#0f766e',
  tealM  : '#0d9488',
  slate  : '#f8fafc',
};

const fmt = (v) => (parseFloat(v) || 0).toLocaleString('id-ID');

/* ──────────────────────────────────────────────────────────────
   SUB-COMPONENTS
────────────────────────────────────────────────────────────── */

/** Sebuah baris form: [label navy | input...] */
const FormRow = ({ label, icon, children, last }) => (
  <div style={{
    display: 'flex', alignItems: 'stretch',
    borderRadius: last ? '0 0 8px 8px' : '0',
    overflow: 'hidden',
    borderBottom: last ? 'none' : `1px solid ${T.border}`,
  }}>
    <div style={{
      background: 'transparent', color: '#475569',
      fontSize: '0.695rem', fontWeight: 700,
      letterSpacing: '0.35px', whiteSpace: 'nowrap',
      padding: '0 12px', minWidth: 160,
      display: 'flex', alignItems: 'center', gap: 6,
      borderRight: `1px solid ${T.border}`,
      flexShrink: 0,
    }}>
      {icon && <i className={`bi ${icon}`} style={{ color: T.sky, fontSize: '0.85rem' }} />}
      {label}
    </div>
    {children}
  </div>
);

/** Sebuah input teks dalam FormRow */
const FInput = React.forwardRef(({ divider, flex, width, style, ...props }, ref) => (
  <input
    ref={ref}
    className="form-control"
    style={{
      height: 32, fontSize: '0.8rem', border: 'none',
      borderRight: divider ? `1px solid ${T.border}` : 'none',
      borderRadius: 0, padding: '0 10px', outline: 'none',
      background: '#fff', color: '#1e293b',
      flex: flex ?? 1,
      width: width,
      minWidth: width,
      ...style
    }}
    {...props}
  />
));

/** Select react-select yang menempel ke kanan FormRow */
const FSelect = ({ options, value, onChange, placeholder, divider, searchable = false, flex, width, style }) => (
  <div style={{ flex: flex ?? 1, width: width, minWidth: width, borderRight: divider ? `1px solid ${T.border}` : 'none', ...style }}>
    <Select
      options={options}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      isSearchable={searchable}
      isClearable={searchable}
      menuPortalTarget={document.body}
      styles={{
        control: b => ({ ...b, minHeight: 32, height: 32, fontSize: '0.8rem', border: 'none', boxShadow: 'none', borderRadius: 0 }),
        valueContainer: b => ({ ...b, padding: '0 10px' }),
        indicatorsContainer: b => ({ ...b, height: 32 }),
        menuPortal: b => ({ ...b, zIndex: 9999 }),
        option: (b, s) => ({
          ...b, fontSize: '0.8rem',
          background: s.isSelected ? T.sky : s.isFocused ? '#f0f9ff' : '#fff',
          color: s.isSelected ? '#fff' : '#1e293b',
        }),
      }}
    />
  </div>
);

/** Section card dengan border */
const Section = ({ title, icon, iconColor = T.sky, children, style = {} }) => (
  <div style={{
    background: '#fff', borderRadius: T.radius,
    border: `1px solid ${T.border}`,
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
    overflow: 'hidden', ...style
  }}>
    <div style={{
      background: T.slate,
      borderBottom: `1px solid ${T.border}`,
      padding: '6px 14px',
      display: 'flex', alignItems: 'center', gap: 7,
    }}>
      <i className={`bi ${icon}`} style={{ color: iconColor, fontSize: '0.9rem' }} />
      <span style={{ fontWeight: 700, fontSize: '0.71rem', color: '#475569', letterSpacing: '0.7px' }}>
        {title}
      </span>
    </div>
    {children}
  </div>
);

/* ──────────────────────────────────────────────────────────────
   MAIN COMPONENT
────────────────────────────────────────────────────────────── */
export default function TrsLaboratorium() {
  const navigate = useNavigate();
  const [time, setTime] = useState({ date: '', clock: '' });
  const user = localStorage.getItem('username') || 'Muhammad Ali';


  /* ── State ── */
  const [jenis, setJenis]     = useState(null);
  const kirsRef = useRef(null);
  const [tipePasien, setTipePasien] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [modalSearch, setModalSearch] = useState('');
  const [modalLoading, setModalLoading] = useState(false);

  const [pasien, setPasien]   = useState({ kirs:'', nama:'', klasifikasi:'', kd_klasifikasi:'', usia:'', pengirim:'', permintaan:'', kelas:'', kd_kelas:'', ruang:'' });

  const [sampel, setSampel]   = useState({
    tgl: new Date().toISOString().split('T')[0],
    jam: ''
  });
  const [rows, setRows] = useState([]);
  const [itemInput, setItemInput] = useState({ jenis: '', kode: '', rincian: '', lab_rujukan: '', tarif: '', dijamin: '', tunai: '' });
  const [dialog, setDialog] = useState({ show: false, type: 'alert', msg: '', title: 'Informasi', confirmPayload: null });
  const [cetakDialog, setCetakDialog] = useState({ show: false, nota: '' });
  const [laporanDialog, setLaporanDialog] = useState({ show: false, tgl1: '', tgl2: '', jenis: '1', loading: false });
  const [pdfUrl, setPdfUrl] = useState('');
  const [printProgress, setPrintProgress] = useState(0);
  const [jenisPemeriksaanOpts, setJenisPemeriksaanOpts] = useState([]);
  const [rincianOpts, setRincianOpts] = useState([]);
  const [labRujukanOpts, setLabRujukanOpts] = useState([]);

  useEffect(() => {
    fetch(`${getApiUrl()}/jenis_tindakan_lab`)
      .then(r => r.json())
      .then(d => {
        const arr = d.list_jenis_tindakan_lab || [];
        setJenisPemeriksaanOpts(arr.map(x => ({ value: x.kode, label: x.jenis })));
      })
      .catch(console.error);

    fetch(`${getApiUrl()}/show_labrujukan`)
      .then(r => r.json())
      .then(d => {
        const arr = d.list_lab || [];
        setLabRujukanOpts(arr.map(x => ({ value: x.kode, label: x.nama })));
      })
      .catch(console.error);
  }, []);

  const fetchRincianOpts = async (jenis) => {
    if (!jenis) {
      setRincianOpts([]);
      return;
    }
    try {
      const r = await fetch(`${getApiUrl()}/show_tindakan_lab?jenis=${jenis}`);
      const d = await r.json();
      const arr = d.list_tindakan_lab || [];
      setRincianOpts(arr.map(x => ({ value: x.kode, label: x.nmtindakan })));
    } catch (err) {
      console.error(err);
    }
  };

  const inlineSelectStyles = {
    control: (base) => ({
      ...base, minHeight: '26px', height: '26px', fontSize: '0.75rem', borderRadius: '4px', borderColor: '#cbd5e1', boxShadow: 'none'
    }),
    valueContainer: (base) => ({ ...base, padding: '0 6px' }),
    input: (base) => ({ ...base, margin: 0, padding: 0 }),
    indicatorsContainer: (base) => ({ ...base, height: '26px' }),
    dropdownIndicator: (base) => ({ ...base, padding: '2px 4px' }),
    option: (base) => ({ ...base, fontSize: '0.75rem', padding: '4px 8px' })
  };

  const handleCheckHarga = async (kodeTindakan, konfirmasi = null) => {
    if (!kodeTindakan || !tipePasien) return;
    
    try {
      const payload = {
        txtjenis: tipePasien.value,
        txtklasifikasi: pasien.kd_klasifikasi || pasien.klasifikasi || '003', // default klasifikasi
        txtkdklas: pasien.kd_kelas || '001', // default umum
        txtkdtindakan2: kodeTindakan
      };
      
      if (konfirmasi) {
        payload[konfirmasi.key] = konfirmasi.value;
      }
      
      const res = await fetch(`${getApiUrl()}/cek_harga_lab`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      
      if (data.status === 2) {
        // Tampilkan custom confirmation modal
        const key = data.confirm_type === 'selisih' ? 'konfirmasi_selisih' : 'konfirmasi_tunai';
        setDialog({ show: true, type: 'confirm', title: 'Konfirmasi Biaya', msg: data.msg, confirmPayload: { kodeTindakan: kodeTindakan, key: key } });
      } else if (data.status === 1) {
        setItemInput(prev => ({
          ...prev,
          tarif: data.data?.biaya || prev.tarif,
          dijamin: data.data?.piutang || 0,
          tunai: data.data?.tunai || 0
        }));
      } else {
        if (data.msg) setDialog({ show: true, type: 'alert', title: 'Peringatan', msg: data.msg, confirmPayload: null });
      }
    } catch (err) {
      console.error('Error cek_harga_lab:', err);
    }
  };

  const handleAddItem = () => {
    if (!sampel.jam) {
      setDialog({ show: true, type: 'alert', title: 'Perhatian', msg: 'Silakan lengkapi Jam Ambil Sampel terlebih dahulu!', confirmPayload: null });
      return;
    }
    if (!itemInput.rincian && !itemInput.kode) return;
    setRows([...rows, {
      jenis: itemInput.jenis,
      kode: itemInput.kode,
      rincian: itemInput.rincian,
      lab_rujukan: itemInput.lab_rujukan,
      tarif: parseFloat(itemInput.tarif) || 0,
      dijamin: parseFloat(itemInput.dijamin) || 0,
      tunai: parseFloat(itemInput.tunai) || 0
    }]);
    setItemInput({ jenis: '', kode: '', rincian: '', lab_rujukan: '', tarif: '', dijamin: '', tunai: '' });
  };
  const [dokterOpts, setDokterOpts] = useState([]);


  /* ── Options ── */
  const tipePasienOpts = [
    { value: '1', label: '1. Pasien Rawat Jalan' },
    { value: '2', label: '2. Pasien Rawat Inap' },
    { value: '3', label: '3. Pasien Tidak Terdaftar (TT)' }
  ];

  /* ── Options ── */
  const jenisOpts = [
    { value:'1', label:'Transaksi Normal' },
    { value:'2', label:'Transaksi Pasien TT' },
    { value:'3', label:'Cetak Ulang Nota' },
    { value:'4', label:'Panggil Nota Belum Bayar' },
    { value:'5', label:'Transaksi Paket Ruangan' },
    { value:'6', label:'Transaksi Cito' },
    { value:'7', label:'Transaksi Cito Pasien TT' },
    { value:'8', label:'Transaksi Paket Penunjang' },
  ];
  const klasOpts = [
    { value:'', label:'-- Klasifikasi --' },
    { value:'UMUM', label:'UMUM' },
    { value:'BPJS', label:'BPJS' },
    { value:'PERUSAHAAN', label:'PERUSAHAAN' },
    { value:'GRATIS', label:'GRATIS' },
  ];

  /* ── Clock ── */
  useEffect(() => {
    const tick = () => {
      const n = new Date();
      setTime({
        date : n.toLocaleDateString('id-ID', { day:'2-digit', month:'long', year:'numeric' }),
        clock: n.toLocaleTimeString('id-ID', { hour:'2-digit', minute:'2-digit', second:'2-digit' }),
      });
    };
    tick();
    const id = setInterval(tick, 1000);

    const fetchDokter = async () => {
      try {
        const res = await fetch(`${getApiUrl()}/show_dokter`);
        const json = await res.json();
        if (json.list_dokter) {
          const opts = json.list_dokter.map(d => ({ value: d.nodok, label: d.nama_dok }));
          setDokterOpts(opts);
        }
      } catch (err) {
        console.error("Error fetching dokter:", err);
      }
    };
    fetchDokter();

    return () => clearInterval(id);
  }, []);

  const hp = (f,v) => setPasien(p => ({ ...p, [f]:v }));
  const hs = (f,v) => setSampel(s => ({ ...s, [f]:v }));


  /* ── Modal Logic ── */
  const getApiUrl = () => localStorage.getItem('apiUrl') || 'http://localhost:8000';
  
  const handleTipePasienChange = (sel) => {
    setTipePasien(sel);
    if (sel && kirsRef.current) {
      setTimeout(() => kirsRef.current.focus(), 100);
    }
  };

  const openPasienModal = async (tipeVal) => {
    setShowModal(true);
    setModalLoading(true);
    setModalData([]);
    setFilteredData([]);
    setModalSearch('');

    let endpoint = '';
    if (tipeVal === '1') endpoint = '/show_pasienrj';
    else if (tipeVal === '2') endpoint = '/show_pasienri';
    else if (tipeVal === '3') endpoint = '/show_pasientt';

    try {
      const res = await fetch(`${getApiUrl()}${endpoint}`);
      const data = await res.json();
      let arr = [];
      if (Array.isArray(data)) arr = data;
      else if (data.list_pasienrj) arr = data.list_pasienrj;
      else if (data.list_pasienri) arr = data.list_pasienri;
      else if (data.data) arr = data.data;
      setModalData(arr);
      setFilteredData(arr);
    } catch (err) { console.error(err); }
    finally { setModalLoading(false); }
  };

  const handleKirsKeyDown = async (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const kirs = pasien.kirs;
      if (!tipePasien) return;
      if (!kirs) {
        openPasienModal(tipePasien.value);
        return;
      }

      if (tipePasien.value === '1') {
        try {
          const res = await fetch(`${getApiUrl()}/caripasienrj?no_medrec=${kirs}`);
          const data = await res.json();
          if (data.status === 1) {
            handleSelectPasien(data);
          } else {
            openPasienModal('1');
          }
        } catch(err) { openPasienModal('1'); }
      } else if (tipePasien.value === '2') {
        try {
          const res = await fetch(`${getApiUrl()}/caripasienri?no_medrec=${kirs}`);
          const data = await res.json();
          if (data.status === 1) {
            handleSelectPasien(data);
          } else {
            openPasienModal('2');
          }
        } catch(err) { openPasienModal('2'); }
      } else if (tipePasien.value === '3') {
         openPasienModal('3');
      }
    }
  };

  const handleModalSearch = (e) => {
    const val = e.target.value;
    setModalSearch(val);
    const lower = val.toLowerCase();
    const filtered = modalData.filter(d => 
      (d.nama && d.nama.toLowerCase().includes(lower)) || 
      (d.no_medrec && d.no_medrec.toLowerCase().includes(lower))
    );
    setFilteredData(filtered);
  };

  const handleSelectPasien = async (p) => {
    let usiaVal = p.usia || '';

    // Hitung usia untuk non-TT
    if (tipePasien && tipePasien.value !== '3' && p.no_medrec) {
      try {
        const res = await fetch(`${getApiUrl()}/hitung_usia?nomedrec=${p.no_medrec}`);
        const data = await res.json();
        if (data.usia) usiaVal = data.usia;
      } catch (err) {
        console.error("Gagal hitung usia", err);
      }
    }

    let defKelas = p.kelas || '';
    let defRuang = p.no_kamar || p.ruangRawat || p.nm_ruang || '';
    let defKdKelas = '';
    
    // Jika RI, tarik data kelas/kamar2 dari caripasienri jika kosong
    if (tipePasien && tipePasien.value === '2' && p.no_medrec) {
      try {
        const res = await fetch(`${getApiUrl()}/caripasienri?no_medrec=${p.no_medrec}`);
        const data = await res.json();
        if (data.status === 1) {
          if (!defKelas) defKelas = data.kelas || '';
          if (!defRuang) defRuang = data.no_kamar || '';
          // Simpan juga sebagai kd_kelas untuk cek harga
          defKdKelas = data.kelas || '';
        }
      } catch(err) {
        console.error("Gagal caripasienri", err);
      }
    }

    if (tipePasien && (tipePasien.value === '1' || tipePasien.value === '3')) {
      defKelas = 'Rawat Jalan UGD';
      defKdKelas = '001';
      defRuang = 'Poliklinik';
    } else if (tipePasien && tipePasien.value === '2') {
      if (!defKdKelas) defKdKelas = defKelas;
    }

    let pengirimName = p.nama_dok || p.nama_dokter || p.nm_dokter || '';
    if (!pengirimName && p.kd_dokter) {
       const doc = dokterOpts.find(d => d.value === p.kd_dokter);
       if (doc) pengirimName = doc.label;
    }

    setPasien({
      kirs: p.no_medrec || '',
      nama: p.nama || p.nm_pasien || '',
      klasifikasi: p.nm_klasifikasi || p.kd_klasifikasi || '',
      kd_klasifikasi: p.kd_klasifikasi || '',
      usia: usiaVal,
      pengirim: pengirimName,
      permintaan: '',
      kelas: defKelas,
      kd_kelas: defKdKelas,
      ruang: defRuang
    });
    setShowModal(false);
  };

  /* ── Totals ── */
  const sT = rows.reduce((a,r) => a + (r.tarif   || 0), 0);
  const sD = rows.reduce((a,r) => a + (r.dijamin || 0), 0);
  const sU = rows.reduce((a,r) => a + (r.tunai   || 0), 0);
  const pT = 0, pD = 0, pU = 0;

  /* ── Col widths (tabel + totals) ── */
  const CW = ['11%', '9%', '21%', '17%', '11%', '11%', '11%', '9%'];
  const CWtot = ['58%', '11%', '11%', '11%', '9%'];

  /* ── Action buttons ── */
  const actions = [
      { label:'PROSES', icon:'bi-gear-fill',        bg:`linear-gradient(135deg,${T.blue},${T.sky})`, fg:'#fff',    border:'none' },
      { label:'HAPUS',  icon:'bi-trash3-fill',       bg:'#fff', fg:'#dc2626', border:'1.5px solid #fca5a5' },
      { label:'CETAK',  icon:'bi-printer-fill',      bg:'#fff', fg:'#0ea5e9', border:'1.5px solid #7dd3fc' },
        { label:'LAPORAN', icon:'bi-file-earmark-text', bg:'#fff', fg:'#10b981', border:'1.5px solid #6ee7b7' },
      { label:'HASIL',  icon:'bi-check-circle-fill', bg:'#fff', fg:'#16a34a', border:'1.5px solid #86efac' },
      { label:'BATAL',  icon:'bi-slash-circle',      bg:'#fff', fg:'#64748b', border:'1.5px solid #cbd5e1' },
    ];

  /* ──────────────────────────────────────────────────────────── */
  const handleCetak = () => {
    setCetakDialog({ show: true, nota: '' });
  };

  const handleCetakLaporan = async () => {
    setLaporanDialog(prev => ({ ...prev, loading: true }));
    let progress = 10;
    setPrintProgress(progress);
    const progressInterval = setInterval(() => {
      progress += 15;
      if (progress > 85) progress = 85;
      setPrintProgress(progress);
    }, 100);

    try {
      const res = await fetch(`${getApiUrl()}/laporan_harian_lab`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tgl1: laporanDialog.tgl1,
          tgl2: laporanDialog.tgl2,
          jenis_laporan: laporanDialog.jenis
        })
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      
      clearInterval(progressInterval);
      setPrintProgress(100);
      
      setTimeout(() => {
        setPrintProgress(0);
        setPdfUrl(url); // Set to modal instead of opening new tab
        setLaporanDialog(prev => ({ ...prev, show: false, loading: false }));
      }, 500);

    } catch(err) {
      clearInterval(progressInterval);
      setPrintProgress(0);
      setDialog({ show: true, type: 'alert', title: 'Error', msg: 'Gagal mencetak laporan: ' + err.message });
      setLaporanDialog(prev => ({ ...prev, loading: false }));
    }
  };


  const handleProses = async () => {
    if (rows.length === 0) {
      setDialog({ show: true, type: 'alert', title: 'Perhatian', msg: 'Data pemeriksaan masih kosong!', confirmPayload: null });
      return;
    }
    if (!pasien.kirs) {
      setDialog({ show: true, type: 'alert', title: 'Perhatian', msg: 'Silakan pilih pasien terlebih dahulu!', confirmPayload: null });
      return;
    }

    const txtnota = "LAB" + Date.now().toString().slice(-6);
    const txtnota2 = Date.now().toString(); // NO URUT
    let successCount = 0;
    
    // Convert YYYY-MM-DD to DD/MM/YYYY
    const tglArr = sampel.tgl.split('-');
    const txttgl = tglArr.length === 3 ? `${tglArr[2]}/${tglArr[1]}/${tglArr[0]}` : sampel.tgl;
    const txtjam2 = (sampel.jam && sampel.jam.length === 5) ? sampel.jam + ':00' : (sampel.jam || '');

    for (const row of rows) {
      const payload = {
        txtnota: txtnota,
        txtnota2: txtnota2,
        txttgl: txttgl,
        txttgl2: sampel.tgl,
        txtjam2: txtjam2,
        txtnomedrec: pasien.kirs,
        txtnmpasien: pasien.nama,
        txtklasifikasi: pasien.kd_klasifikasi || pasien.klasifikasi,
        txtkdklas: pasien.kd_kelas || '001',
        txtkddokter: dokterOpts.find(d => d.label === pasien.pengirim)?.value || "",
        txtdokter: pasien.pengirim,
        txtnopermintaan: pasien.permintaan,
        txtkdtindakan: row.kode,
        txtnmtindakan: row.rincian,
        txtharga: row.tarif,
        txtkdlab2: labRujukanOpts.find(l => l.label === row.lab_rujukan)?.value || "",
        txtnmlab2: row.lab_rujukan || "",
        cmbjenis: row.jenis,
        txtkddokterpa: "",
        txtnmdokterpa: "",
        xNoLab: txtnota,
        xHrgRujuk: 0,
        xdefabayar: "",
        xJns: "",
        xjasa: 0,
        xbahantdk: 0,
        xsewa: 0,
        xpiutang: row.dijamin,
        xTotaljs2: 0,
        xtunaij: row.tunai,
        xpiutangj: row.dijamin,
        xpiutangjjs: 0,
        xtunaib: row.tunai,
        xpiutangb: row.dijamin,
        xpiutangbjs: 0,
        xtunais: row.tunai,
        xpiutangs: row.dijamin,
        xpiutangsjs: 0
      };

      try {
        const res = await fetch(`${getApiUrl()}/simpan_data_lab`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        const data = await res.json();
        if (data.status === 1) {
          successCount++;
        } else {
          setDialog({ show: true, type: 'alert', title: 'Gagal', msg: data.msg || 'Terjadi kesalahan sistem', confirmPayload: null });
          return;
        }
      } catch (err) {
        console.error("Error saving row:", row, err);
        setDialog({ show: true, type: 'alert', title: 'Error', msg: 'Koneksi ke server gagal!', confirmPayload: null });
        return;
      }
    }
    
    if (successCount === rows.length) {
       setDialog({ show: true, type: 'alert', title: 'Berhasil', msg: `Berhasil menyimpan ${successCount} data lab!`, confirmPayload: null });
       setRows([]); 
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(160deg,#f0f4f8 0%,#e8f0fe 100%)',
      fontFamily: "'Inter','Segoe UI',sans-serif",
      display: 'flex', flexDirection: 'column',
    }}>

      {/* ═══════════════════════ TOP NAV ═══════════════════════════ */}
      <div style={{
        height: T.navH,
        background: `linear-gradient(135deg,${T.navy} 0%,${T.blue} 55%,${T.sky} 100%)`,
        padding: '0 20px', flexShrink: 0,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        boxShadow: '0 2px 16px rgba(0,0,0,0.32)',
      }}>
        {/* Left */}
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <div style={{ background:'rgba(255,255,255,0.13)', borderRadius:8, padding:'6px 10px', display:'flex', alignItems:'center' }}>
            <i className="bi bi-droplet-half" style={{ color:'#fbbf24', fontSize:'1.15rem' }} />
          </div>
          <div style={{ lineHeight:1.35 }}>
            <div style={{ color:'#fff', fontWeight:800, fontSize:'0.95rem', letterSpacing:'0.6px' }}>
              TRANSAKSI LABORATORIUM
            </div>
            <div style={{ color:'rgba(255,255,255,0.5)', fontSize:'0.68rem' }}>
              Petugas: <span style={{ color:'rgba(255,255,255,0.8)', fontWeight:600 }}>{user}</span>
            </div>
          </div>
        </div>

        {/* Right */}
        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
          {/* Antrian chip */}
          <div style={{ background:'rgba(0,0,0,0.25)', borderRadius:20, padding:'3px 14px', display:'flex', alignItems:'center', gap:6 }}>
            <i className="bi bi-people-fill" style={{ color:'#34d399', fontSize:'0.82rem' }} />
            <span style={{ color:'#34d399', fontFamily:'monospace', fontWeight:700, fontSize:'0.82rem' }}>Antrian: 0</span>
          </div>
          {/* Clock */}
          <div style={{ textAlign:'right', lineHeight:1.3 }}>
            <div style={{ color:'#fff', fontFamily:'monospace', fontWeight:700, fontSize:'0.8rem', letterSpacing:'1px' }}>{time.clock}</div>
            <div style={{ color:'rgba(255,255,255,0.45)', fontSize:'0.65rem' }}>{time.date}</div>
          </div>
          {/* Home btn */}
          <button
            onClick={() => navigate('/home')}
            style={{
              background:'rgba(255,255,255,0.12)', border:'1px solid rgba(255,255,255,0.28)',
              color:'#fff', borderRadius:8, padding:'5px 14px',
              fontSize:'0.78rem', fontWeight:700, cursor:'pointer',
              display:'flex', alignItems:'center', gap:5,
              transition:'background .15s',
            }}
          >
            <i className="bi bi-house-fill" />Home
          </button>
        </div>
      </div>

      {/* ═══════════════════════ CONTENT ═══════════════════════════ */}
      <div style={{
        flex:1, padding:'12px 14px',
        display:'flex', flexDirection:'column', gap:10, overflow:'hidden',
      }}>

        
        <div style={{ display: 'flex', gap: 10 }}>
          {/* ── JENIS TRANSAKSI ──────────────────────────────────────── */}
          <div style={{
            flex: 1,
            background:'linear-gradient(90deg, #fff1f2 0%, #ffe4e6 100%)',
            borderRadius: T.radius,
            display:'flex', alignItems:'stretch',
            boxShadow:'0 1px 3px rgba(225,29,72,0.1)',
            border: '1px solid #fecdd3',
            overflow:'hidden',
          }}>
            <div style={{
              padding:'0 18px', display:'flex', alignItems:'center', gap:8,
              color:'#9f1239', fontWeight:800, fontSize:'0.75rem', letterSpacing:'0.5px',
              borderRight:'1px solid #fecdd3', whiteSpace:'nowrap', flexShrink:0,
            }}>
              <i className="bi bi-list-check" style={{ color:'#e11d48', fontSize:'1rem' }} />
              JENIS TRANSAKSI
            </div>
            <div style={{ flex:1, padding:'5px 8px' }}>
              <Select
                options={jenisOpts}
                value={jenis}
                onChange={setJenis}
                placeholder="Cari atau pilih jenis transaksi..."
                isSearchable
                isClearable
                menuPortalTarget={document.body}
                formatOptionLabel={o => (
                  <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                    <span style={{
                      background:T.sky, color:'#fff', borderRadius:5,
                      padding:'1px 7px', fontSize:'0.68rem', fontWeight:800,
                      minWidth:22, textAlign:'center',
                    }}>{o.value}</span>
                    <span style={{ fontSize:'0.83rem', fontWeight:600, color:'#1e293b' }}>{o.label}</span>
                  </div>
                )}
                styles={{
                  control: b => ({ ...b, minHeight:34, height:34, fontSize:'0.83rem', border:'none', boxShadow:'none', borderRadius:6 }),
                  valueContainer: b => ({ ...b, padding:'0 8px' }),
                  indicatorsContainer: b => ({ ...b, height:34 }),
                  menuPortal: b => ({ ...b, zIndex:9999 }),
                  option: (b,s) => ({ ...b, fontSize:'0.82rem', background: s.isSelected ? T.sky : s.isFocused ? '#f0f9ff' : '#fff', color: s.isSelected ? '#fff' : '#1e293b' }),
                }}
              />
            </div>
          </div>

          {/* ── PILIH PASIEN ──────────────────────────────────────── */}
          <div style={{
            flex: 1,
            background:'linear-gradient(90deg, #f0fdf4 0%, #dcfce7 100%)',
            borderRadius: T.radius,
            display:'flex', alignItems:'stretch',
            boxShadow:'0 1px 3px rgba(22,163,74,0.1)',
            border: '1px solid #bbf7d0',
            overflow:'hidden',
          }}>
            <div style={{
              padding:'0 18px', display:'flex', alignItems:'center', gap:8,
              color:'#166534', fontWeight:800, fontSize:'0.75rem', letterSpacing:'0.5px',
              borderRight:'1px solid #bbf7d0', whiteSpace:'nowrap', flexShrink:0,
            }}>
              <i className="bi bi-person-fill-add" style={{ color:'#16a34a', fontSize:'1rem' }} />
              SUMBER PASIEN
            </div>
            <div style={{ flex:1, padding:'5px 8px' }}>
              <Select
                options={tipePasienOpts}
                value={tipePasien}
                onChange={handleTipePasienChange}
                placeholder="Pilih Sumber Pasien..."
                isClearable
                menuPortalTarget={document.body}
                styles={{
                  control: b => ({ ...b, minHeight:34, height:34, fontSize:'0.83rem', border:'none', boxShadow:'none', borderRadius:6 }),
                  valueContainer: b => ({ ...b, padding:'0 8px' }),
                  indicatorsContainer: b => ({ ...b, height:34 }),
                  menuPortal: b => ({ ...b, zIndex:9999 }),
                }}
              />
            </div>
          </div>
        </div>

        {/* ── PASIEN + SAMPEL ──────────────────────────────────────── */}
        <div style={{ display:'flex', gap:10 }}>

          {/* Data Pasien */}
          <Section title="DATA PASIEN" icon="bi-person-badge-fill" iconColor={T.sky} style={{ flex:7 }}>
            <div style={{ display:'flex', flexDirection:'column', border:`1px solid ${T.border}`, borderTop:'none', borderRadius:`0 0 ${T.radius}px ${T.radius}px` }}>
              <FormRow label="KIRS | NAMA PASIEN" icon="bi-credit-card-2-front">
                <FInput ref={kirsRef} flex={0} width={110} placeholder="No. KIRS / RM" value={pasien.kirs} onChange={e=>hp('kirs',e.target.value)} onKeyDown={handleKirsKeyDown} divider />
                <FInput placeholder="Nama Lengkap Pasien" value={pasien.nama} onChange={e=>hp('nama',e.target.value)} />
              </FormRow>
              <FormRow label="PENGIRIM | PERMINTAAN" icon="bi-person-video3">
                <FSelect 
                  options={dokterOpts}
                  value={dokterOpts.find(o => o.label === pasien.pengirim) || (pasien.pengirim ? { label: pasien.pengirim, value: pasien.pengirim } : null)}
                  onChange={sel => hp('pengirim', sel ? sel.label : '')}
                  placeholder="Pilih Dokter Pengirim..."
                  divider
                  searchable
                />
                <FInput placeholder="No. Permintaan" value={pasien.permintaan} onChange={e=>hp('permintaan',e.target.value)} />
              </FormRow>
              <FormRow label="KLASIFIKASI | USIA" icon="bi-shield-check" last>
                <FSelect
                  options={klasOpts}
                  value={klasOpts.find(o=>o.value===pasien.klasifikasi)||null}
                  onChange={v=>hp('klasifikasi',v?.value||'')}
                  placeholder="-- Klasifikasi --"
                  divider
                />
                <FInput flex={0} width={70} placeholder="Usia" value={pasien.usia} onChange={e=>hp('usia',e.target.value)} divider style={{ textAlign:'center' }} />
                <div style={{
                  background: 'transparent', color:'#475569', fontSize:'0.695rem', fontWeight:700,
                  letterSpacing:'0.35px', whiteSpace:'nowrap', padding:'0 12px',
                  display:'flex', alignItems:'center', gap:6,
                  borderRight:`1px solid ${T.border}`, flexShrink:0,
                }}>
                  <i className="bi bi-door-open" style={{ color: T.sky, fontSize: '0.85rem' }} /> KELAS | R. RAWAT
                </div>
                <FInput flex={0} width={75} placeholder="Kelas" value={pasien.kelas} onChange={e=>hp('kelas',e.target.value)} divider style={{ textAlign:'center' }} />
                <FInput placeholder="Ruang Rawat" value={pasien.ruang} onChange={e=>hp('ruang',e.target.value)} />
              </FormRow>
            </div>
          </Section>

          {/* Sampel */}
          <Section title="PENGAMBILAN SAMPEL" icon="bi-eyedropper" iconColor="#dc2626" style={{ flex:5 }}>
            <div style={{ display:'flex', flexDirection:'column', border:`1px solid ${T.border}`, borderTop:'none', borderRadius:`0 0 ${T.radius}px ${T.radius}px` }}>
              <FormRow label="TGL | JAM AMBIL SAMPLE" icon="bi-calendar-event" last>
                <FInput type="date" flex={0} width={150} value={sampel.tgl} onChange={e=>hs('tgl',e.target.value)} divider />
                <FInput type="time" value={sampel.jam} onChange={e=>hs('jam',e.target.value)} />
              </FormRow>
            </div>
          </Section>

        </div>

        {/* ── TABEL PEMERIKSAAN ─────────────────────────────────────── */}
        <div style={{
          flex:1, display:'flex', flexDirection:'column',
          background:'#fff', borderRadius: T.radius,
          border:`1px solid ${T.border}`,
          boxShadow:'0 1px 3px rgba(0,0,0,0.06)',
          overflow:'hidden', minHeight:180,
        }}>
          {/* thead */}
          <div style={{ background:`linear-gradient(90deg,${T.teal},${T.tealM})`, flexShrink:0 }}>
            <table style={{ width:'100%', tableLayout:'fixed', borderCollapse:'collapse', fontSize:'0.76rem', color:'#fff' }}>
              <colgroup>{CW.map((w,i)=><col key={i} style={{width:w}}/>)}</colgroup>
              <thead>
                <tr>
                  <th colSpan={4} style={{ padding:'8px 12px', textAlign:'center', borderRight:'1px solid rgba(255,255,255,.2)', borderBottom:'1px solid rgba(255,255,255,.15)', letterSpacing:'1.5px', fontWeight:800 }}>
                    PEMERIKSAAN
                  </th>
                  {['TARIF [Rp]','DIJAMIN [Rp]','TUNAI [Rp]'].map((h,i)=>(
                    <th key={i} style={{ padding:'8px 10px', textAlign:'right', fontWeight:700, borderRight: '1px solid rgba(255,255,255,.2)', borderBottom:'1px solid rgba(255,255,255,.15)' }}>{h}</th>
                  ))}
                
                    <th style={{ borderBottom:'1px solid rgba(255,255,255,.15)' }}></th>
                  </tr>
                <tr style={{ background:'rgba(0,0,0,0.14)', fontSize:'0.69rem', color:'rgba(224,242,241,0.85)', fontWeight:500 }}>
                  <th style={{ padding:'3px 10px', textAlign:'center', borderRight:'1px solid rgba(255,255,255,.15)' }}>JENIS</th>
                  <th style={{ padding:'3px 10px', borderRight:'1px solid rgba(255,255,255,.15)' }}>RINCIAN</th>
                  <th colSpan={3} style={{ borderRight:"1px solid rgba(255,255,255,.15)" }} />
                  <th style={{ textAlign:"center" }}>AKSI</th>
                </tr>
              </thead>
            </table>
          </div>

          {/* tbody scroll area */}
          <div style={{ flex:1, overflow:'auto' }}>
            <table style={{ width:'100%', tableLayout:'fixed', borderCollapse:'collapse', fontSize:'0.78rem' }}>
              <colgroup>{CW.map((w,i)=><col key={i} style={{width:w}}/>)}</colgroup>
              <tbody>                  {/* BARIS INPUT */}
                  <tr style={{ background: '#f8fafc', borderBottom: '2px solid #0284c7', opacity: (!sampel.jam) ? 0.6 : 1, pointerEvents: (!sampel.jam) ? 'none' : 'auto' }}>
                    <td style={{ padding: '4px 6px' }}>
                      <Select
                        styles={inlineSelectStyles}
                        placeholder="Pilih Jenis..."
                        options={jenisPemeriksaanOpts}
                        value={jenisPemeriksaanOpts.find(o => o.label === itemInput.jenis) || null}
                        onChange={v => {
                          setItemInput({...itemInput, jenis: v?.label || '', kode: '', rincian: ''});
                          fetchRincianOpts(v?.label || '');
                        }}
                        isClearable
                      />
                    </td>
                    <td style={{ padding: '4px 6px' }}>
                      <input className="form-control form-control-sm text-center" placeholder="Kode" style={{ fontSize: '0.75rem', padding: '4px 6px', background: '#e2e8f0', cursor: 'not-allowed' }} value={itemInput.kode} readOnly tabIndex={-1} />
                    </td>
                    <td style={{ padding: '4px 6px' }}>
                      <Select
                        styles={inlineSelectStyles}
                        placeholder="Pilih Pemeriksaan..."
                        options={rincianOpts}
                        value={rincianOpts.find(o => o.value === itemInput.kode) || null}
                        onChange={v => {
                          setItemInput({...itemInput, kode: v?.value || '', rincian: v?.label || ''});
                          if (v?.value) handleCheckHarga(v.value);
                        }}
                        isClearable
                      />
                    </td>
                    <td style={{ padding: '4px 6px' }}>
                      <Select
                        styles={inlineSelectStyles}
                        placeholder="Pilih Lab..."
                        options={labRujukanOpts}
                        value={labRujukanOpts.find(o => o.label === itemInput.lab_rujukan) || null}
                        onChange={v => {
                          setItemInput({...itemInput, lab_rujukan: v?.label || ''});
                          if (itemInput.kode) handleCheckHarga(itemInput.kode);
                        }}
                        isClearable
                      />
                    </td>
                    <td style={{ padding: '6px' }}>
                      <input type="number" className="form-control form-control-sm text-end" placeholder="Tarif" value={itemInput.tarif} readOnly tabIndex={-1} style={{ fontSize: '0.75rem', padding: '4px 6px', background: '#e2e8f0', cursor: 'not-allowed' }} onKeyDown={e => e.key === 'Enter' && handleAddItem()} />
                    </td>
                    <td style={{ padding: '6px' }}>
                      <input type="number" className="form-control form-control-sm text-end" placeholder="Dijamin" value={itemInput.dijamin} readOnly tabIndex={-1} style={{ fontSize: '0.75rem', padding: '4px 6px', background: '#e2e8f0', cursor: 'not-allowed' }} onKeyDown={e => e.key === 'Enter' && handleAddItem()} />
                    </td>
                    <td style={{ padding: '6px' }}>
                      <input type="number" className="form-control form-control-sm text-end" placeholder="Tunai" value={itemInput.tunai} readOnly tabIndex={-1} style={{ fontSize: '0.75rem', padding: '4px 6px', background: '#e2e8f0', cursor: 'not-allowed' }} onKeyDown={e => e.key === 'Enter' && handleAddItem()} />
                    </td>
                    <td style={{ padding: '6px', textAlign: 'center' }}>
                      <button className="btn btn-sm btn-primary" style={{ width: '100%', fontSize: '0.75rem', fontWeight: 'bold', padding: '4px 0' }} onClick={handleAddItem}><i className="bi bi-plus-lg"></i></button>
                    </td>
                  </tr>
                {rows.length > 0 ? rows.map((item,idx)=>(
                  <tr key={idx} style={{ background: idx%2===0?'#fff':'#f8fafc', borderBottom:`1px solid ${T.border}` }}>
                    <td style={{ padding:'5px 10px', textAlign:'center', color:T.sky, fontWeight:700 }}>{item.jenis}</td>
                    <td style={{ padding:'5px 10px', textAlign:'center', color:'#64748b', fontWeight:600 }}>{item.kode}</td>
                    <td style={{ padding:'5px 10px', color:'#334155' }}>{item.rincian}</td>
                    <td style={{ padding:'5px 10px', color:'#475569' }}>{item.lab_rujukan || '-'}</td>
                    <td style={{ padding:'5px 10px', textAlign:'right', color:'#334155' }}>{fmt(item.tarif)}</td>
                    <td style={{ padding:'5px 10px', textAlign:'right', color:'#16a34a' }}>{fmt(item.dijamin)}</td>
                    <td style={{ padding:'5px 10px', textAlign:'right', fontWeight:700, color:T.blue }}>{fmt(item.tunai)}</td>
                      <td style={{ padding:"5px 10px", textAlign:"center" }}>
                        <button className="btn btn-sm btn-outline-danger py-0 px-2" onClick={() => setRows(rows.filter((_, i) => i !== idx))}><i className="bi bi-trash"></i></button>
                      </td>
                    </tr>
                )) : (
                  <tr>
                    <td colSpan={8} style={{ textAlign:'center', padding:'48px 0', color:T.muted }}>
                      <i className="bi bi-clipboard2-pulse" style={{ fontSize:'2.4rem', opacity:.2, display:'block', marginBottom:8 }} />
                      <span style={{ fontSize:'0.8rem' }}>Belum ada data pemeriksaan</span>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* totals footer */}
          <div style={{ background:T.navy, flexShrink:0, borderTop:`2px solid ${T.blue}` }}>
            <table style={{ width:'100%', tableLayout:'fixed', borderCollapse:'collapse', fontSize:'0.77rem' }}>
              <colgroup>{CWtot.map((w,i)=><col key={i} style={{width:w}}/>)}</colgroup>
              <tbody>
                {[
                  { label:'SUB TOTAL',    v:[sT,sD,sU], color:'#6ee7b7', bold:false },
                  { label:'PERLENGKAPAN', v:[pT,pD,pU], color:'#fde68a', bold:false },
                  { label:'TOTAL',        v:[sT+pT,sD+pD,sU+pU], color:'#fca5a5', bold:true },
                ].map((row,i)=>(
                  <tr key={i} style={{
                    borderTop:'1px solid rgba(255,255,255,.07)',
                    background: row.bold ? 'rgba(255,255,255,.05)' : 'transparent',
                  }}>
                    <td style={{ padding: row.bold?'9px 16px':'5px 16px', color:'#e2e8f0', fontWeight: row.bold?800:600, fontSize: row.bold?'0.84rem':'0.76rem', letterSpacing: row.bold?'0.6px':0 }}>
                      {row.label}
                      <span style={{ marginLeft:6, fontWeight:300, fontSize:'0.65rem', color:'rgba(255,255,255,0.3)' }}>[Rp]</span>
                    </td>
                    {row.v.map((v,j)=>(
                      <td key={j} style={{ padding: row.bold?'9px 12px':'5px 12px', textAlign:'right', color:row.color, fontFamily:'monospace', fontWeight: row.bold?800:600, fontSize: row.bold?'0.88rem':'0.77rem' }}>
                        {fmt(v)}
                      </td>
                    ))}
                      <td />
                    </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* ── ACTION BUTTONS ───────────────────────────────────────── */}
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', flexShrink:0, paddingTop:2 }}>
          <div style={{ display:'flex', gap:6 }}>
            {actions.map((btn,i)=>(
              <button key={i} onClick={() => { 
                if(btn.label === 'PROSES') handleProses(); 
                else if(btn.label === 'CETAK') handleCetak();
                else if(btn.label === 'LAPORAN') {
                    const today = new Date().toISOString().split('T')[0];
                    setLaporanDialog({ show: true, tgl1: today, tgl2: today, jenis: '1', loading: false });
                }
                else if(btn.label === 'KELUAR') navigate('/home');
                  else if(btn.label === 'RUANG RAWAT') navigate('/TrsRuangRawat');
              }} style={{
                background: btn.bg, color: btn.fg, border: btn.border,
                borderRadius:8, padding:'7px 16px',
                fontWeight:700, fontSize:'0.78rem', cursor:'pointer',
                display:'flex', alignItems:'center', gap:5,
                boxShadow:'0 1px 4px rgba(0,0,0,0.1)',
                letterSpacing:'0.3px',
              }}>
                <i className={`bi ${btn.icon}`} /> {btn.label}
              </button>
            ))}
          </div>
          <button
            onClick={()=>navigate('/home')}
            style={{
              background:'#fff', color:'#dc2626',
              border:'1.5px solid #fca5a5', borderRadius:8,
              padding:'7px 18px', fontWeight:700, fontSize:'0.78rem',
              cursor:'pointer', display:'flex', alignItems:'center', gap:6,
              boxShadow:'0 1px 4px rgba(0,0,0,0.08)',
            }}
          >
            <i className="bi bi-box-arrow-right" /> KELUAR
          </button>
        </div>

      </div>

            {/* Modal PDF Viewer */}
      <Modal show={!!pdfUrl} onHide={() => setPdfUrl('')} size="xl" centered backdrop="static" keyboard={false}>
        <div style={{ background: '#1e293b', padding: '12px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderRadius: '8px 8px 0 0' }}>
          <div style={{ color: '#fff', fontWeight: 700, fontSize: '1rem', letterSpacing: '0.5px' }}>
            <i className="bi bi-file-earmark-pdf-fill me-2" style={{ color: '#ef4444' }}></i> Pratinjau Cetak Nota
          </div>
          <button onClick={() => setPdfUrl('')} style={{ background: 'transparent', border: 'none', color: '#cbd5e1', fontSize: '1.5rem', cursor: 'pointer' }}>&times;</button>
        </div>
        <Modal.Body style={{ height: '80vh', padding: 0, background: '#f1f5f9' }}>
          {pdfUrl && <iframe src={pdfUrl} width="100%" height="100%" style={{ border: 'none' }} title="PDF Viewer" />}
        </Modal.Body>
      </Modal>

      {/* MODAL PENCARIAN PASIEN */} 
      
      {/* DIALOG LAPORAN */}
      {laporanDialog.show && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(15,23,42,0.4)', backdropFilter: 'blur(6px)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#ffffff', borderRadius: '24px', width: '480px', padding: '32px', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25), 0 0 0 1px rgba(0,0,0,0.05)' }}>
            
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '32px' }}>
              <div style={{ width: '56px', height: '56px', borderRadius: '16px', background: 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#059669', fontSize: '1.6rem', boxShadow: '0 4px 10px rgba(16, 185, 129, 0.15)' }}>
                <i className="bi bi-graph-up-arrow"></i>
              </div>
              <div>
                <h4 style={{ margin: 0, fontSize: '1.3rem', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.3px' }}>Laporan Harian</h4>
                <div style={{ fontSize: '0.85rem', color: '#64748b', marginTop: '2px', fontWeight: 500 }}>Konfigurasi parameter cetak laporan lab</div>
              </div>
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', marginBottom: '36px' }}>
              
              <div style={{ background: '#f8fafc', padding: '16px', borderRadius: '16px', border: '1px solid #f1f5f9' }}>
                <label style={{ fontSize: '0.8rem', fontWeight: 700, color: '#334155', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  <i className="bi bi-calendar3" style={{ color: '#0ea5e9' }}></i> Rentang Tanggal
                </label>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                  <div style={{ flex: 1 }}>
                    <input type="date" className="form-control form-control-sm" value={laporanDialog.tgl1} onChange={e => setLaporanDialog({...laporanDialog, tgl1: e.target.value})} style={{ borderRadius: '10px', border: '1.5px solid #e2e8f0', padding: '0.6rem 0.8rem', fontSize: '0.85rem', fontWeight: 600, color: '#1e293b', boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.02)' }} />
                  </div>
                  <div style={{ color: '#94a3b8', fontWeight: 600 }}>
                    <i className="bi bi-arrow-right"></i>
                  </div>
                  <div style={{ flex: 1 }}>
                    <input type="date" className="form-control form-control-sm" value={laporanDialog.tgl2} onChange={e => setLaporanDialog({...laporanDialog, tgl2: e.target.value})} style={{ borderRadius: '10px', border: '1.5px solid #e2e8f0', padding: '0.6rem 0.8rem', fontSize: '0.85rem', fontWeight: 600, color: '#1e293b', boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.02)' }} />
                  </div>
                </div>
              </div>

              <div>
                <label style={{ fontSize: '0.85rem', fontWeight: 700, color: '#334155', marginBottom: '8px', display: 'block' }}>Dasar Laporan</label>
                <div style={{ position: 'relative' }}>
                  <select className="form-select" value={laporanDialog.jenis} onChange={e => setLaporanDialog({...laporanDialog, jenis: e.target.value})} style={{ borderRadius: '12px', border: '1.5px solid #e2e8f0', padding: '0.65rem 0.8rem', fontSize: '0.9rem', fontWeight: 600, color: '#1e293b', background: '#fff', cursor: 'pointer', appearance: 'none' }}>
                    <option value="1">1. Pemeriksaan (Detail Tindakan)</option>
                    <option value="2">2. Nota (Header Transaksi)</option>
                  </select>
                  <i className="bi bi-chevron-down" style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)', color: '#64748b', pointerEvents: 'none', fontWeight: 700 }}></i>
                </div>
              </div>
            </div>

            {printProgress > 0 && laporanDialog.loading && (
              <div style={{ marginBottom: '20px' }}>
                <ProgressBar animated now={printProgress} label={`${printProgress}%`} variant="success" style={{ height: '1.2rem', borderRadius: '8px' }} />
                <div style={{ fontSize: '0.85rem', color: '#64748b', marginTop: '8px', fontWeight: 600 }}>Sedang menyusun laporan...</div>
              </div>
            )}

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', paddingTop: '8px', borderTop: '1px solid #f1f5f9' }}>
              <button className="btn btn-light" style={{ padding: '0.65rem 1.5rem', fontWeight: 700, borderRadius: '12px', border: '1.5px solid #e2e8f0', color: '#64748b', background: '#fff' }} onClick={() => setLaporanDialog({...laporanDialog, show: false})}>
                Batal
              </button>
              <button className="btn btn-success" style={{ padding: '0.65rem 2rem', fontWeight: 700, borderRadius: '12px', background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', border: 'none', color: '#fff', display: 'flex', alignItems: 'center', gap: '8px', boxShadow: '0 4px 12px rgba(16, 185, 129, 0.25)', letterSpacing: '0.3px', minWidth: '120px', justifyContent: 'center' }} onClick={handleCetakLaporan} disabled={laporanDialog.loading}>
                {laporanDialog.loading ? <i className="bi bi-arrow-repeat" style={{display:'inline-block',animation:'spin .8s linear infinite'}}></i> : <><i className="bi bi-printer"></i> Cetak Laporan</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Cetak Ulang Nota */}
      <Modal show={cetakDialog.show} onHide={() => setCetakDialog({ show: false, nota: '' })} centered backdrop="static" keyboard={false}>
        <Modal.Body style={{ padding: '32px 24px', textAlign: 'center' }}>
          <div style={{ marginBottom: '24px' }}>
            <div style={{ width: 72, height: 72, borderRadius: '50%', background: '#e0f2fe', color: '#0ea5e9', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto', fontSize: '2.5rem', boxShadow: '0 4px 10px rgba(14, 165, 233, 0.15)' }}>
              <i className="bi bi-printer-fill"></i>
            </div>
          </div>
          <h5 style={{ fontWeight: 800, color: '#1e293b', marginBottom: '16px', fontSize: '1.25rem' }}>Cetak Ulang Nota</h5>
          <p style={{ color: '#64748b', fontSize: '0.95rem', marginBottom: '24px', padding: '0 10px', lineHeight: '1.5' }}>
            Masukkan Nomor Nota Laboratorium yang ingin Anda cetak ulang.
          </p>
          <div style={{ padding: '0 20px', marginBottom: '32px' }}>
              <input 
                type="text" 
                className="form-control text-center" 
                placeholder="Contoh: LAB2608001" 
                value={cetakDialog.nota}
                onChange={(e) => setCetakDialog({ ...cetakDialog, nota: e.target.value })}
                style={{ fontSize: '1.1rem', fontWeight: 700, padding: '10px' }}
                autoFocus
                disabled={printProgress > 0}
              />
              {printProgress > 0 && (
                <div style={{ marginTop: '20px' }}>
                  <ProgressBar animated now={printProgress} label={`${printProgress}%`} variant="info" style={{ height: '1.2rem', borderRadius: '8px' }} />
                  <div style={{ fontSize: '0.85rem', color: '#64748b', marginTop: '8px', fontWeight: 600 }}>Sedang menyusun dokumen cetak...</div>
                </div>
              )}
            </div>
          <div style={{ display: 'flex', gap: '14px', justifyContent: 'center' }}>
            <button 
              className="btn btn-light" 
              style={{ padding: '10px 28px', fontWeight: 700, color: '#64748b', border: '1px solid #cbd5e1', borderRadius: '8px' }}
              onClick={() => setCetakDialog({ show: false, nota: '' })}
            >
              Batal
            </button>
            <button 
              className="btn btn-primary" 
              style={{ padding: '10px 28px', fontWeight: 700, background: '#0ea5e9', border: 'none', borderRadius: '8px', boxShadow: '0 4px 6px rgba(14, 165, 233, 0.2)' }}
              disabled={printProgress > 0}
              onClick={async () => {
                if (!cetakDialog.nota) {
                  setDialog({ show: true, type: 'alert', title: 'Perhatian', msg: 'Nomor Nota tidak boleh kosong!', confirmPayload: null });
                  return;
                }
                
                const tglArr = sampel.tgl.split('-');
                const txttgl = tglArr.length === 3 ? `${tglArr[2]}/${tglArr[1]}/${tglArr[0]}` : sampel.tgl;
                
                const payload = {
                  txtnota: cetakDialog.nota,
                  txtnota2: cetakDialog.nota,
                  txttgl2: txttgl,
                  txtnomedrec: pasien.kirs || "",
                  txtnmpasien: pasien.nama || "",
                  txtnmklas: pasien.kelas || "Kelas 1",
                  txtnmklasifikasi: pasien.klasifikasi || "Umum",
                  txtklasifikasi: pasien.kd_klasifikasi || "001",
                  txtnopermintaan: pasien.permintaan || "",
                  txtjenis: tipePasien ? tipePasien.value : "1",
                  txtnmpaket: "",
                  txtnmlokasi: "",
                  txtdokter: pasien.pengirim || "",
                  txtadm: 15000,
                  xctkrinci: "Y"
                };

                setPrintProgress(10);
                const progressInterval = setInterval(() => {
                  setPrintProgress(prev => (prev < 90 ? prev + 15 : prev));
                }, 500);

                try {
                  const res = await fetch(`${getApiUrl()}/cetak_nota_lab`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload)
                  });
                  
                  clearInterval(progressInterval);
                  
                  if (!res.ok) throw new Error("Gagal mengambil PDF");
                  
                  setPrintProgress(100);
                  const blob = await res.blob();
                  const url = window.URL.createObjectURL(blob);
                  
                  setTimeout(() => {
                      setPrintProgress(0);
                      setPdfUrl(url);
                      setCetakDialog({ show: false, nota: '' });
                  }, 500);
                  
                } catch (err) {
                  clearInterval(progressInterval);
                  setPrintProgress(0);
                  console.error("Print error:", err);
                  setDialog({ show: true, type: 'alert', title: 'Error', msg: 'Gagal mencetak nota! Pastikan server menyala.', confirmPayload: null });
                }
              }}
            >
              {printProgress > 0 ? 'Memproses...' : 'Cetak Sekarang'}
            </button>
          </div>
        </Modal.Body>
      </Modal>

      {/* Modal Unified Alert / Confirm */}
      <Modal show={dialog.show} onHide={() => setDialog({ ...dialog, show: false })} centered backdrop="static" keyboard={false}>
        <Modal.Body style={{ padding: '32px 24px', textAlign: 'center' }}>
          <div style={{ marginBottom: '24px' }}>
            <div style={{ width: 72, height: 72, borderRadius: '50%', background: dialog.type === 'confirm' ? '#fef3c7' : '#fee2e2', color: dialog.type === 'confirm' ? '#d97706' : '#ef4444', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto', fontSize: '2.5rem', boxShadow: dialog.type === 'confirm' ? '0 4px 10px rgba(217, 119, 6, 0.15)' : '0 4px 10px rgba(239, 68, 68, 0.15)' }}>
              <i className={`bi ${dialog.type === 'confirm' ? 'bi-exclamation-triangle' : 'bi-x-circle'}`}></i>
            </div>
          </div>
          <h5 style={{ fontWeight: 800, color: '#1e293b', marginBottom: '16px', fontSize: '1.25rem' }}>{dialog.title}</h5>
          <p style={{ color: '#64748b', fontSize: '1rem', marginBottom: '32px', padding: '0 10px', lineHeight: '1.5' }}>{dialog.msg}</p>
          <div style={{ display: 'flex', gap: '14px', justifyContent: 'center' }}>
            {dialog.type === 'confirm' ? (
              <>
                <button 
                  className="btn btn-light" 
                  style={{ padding: '10px 28px', fontWeight: 700, color: '#64748b', border: '1px solid #cbd5e1', borderRadius: '8px' }}
                  onClick={() => setDialog({ ...dialog, show: false })}
                >
                  Batalkan
                </button>
                <button 
                  className="btn btn-primary" 
                  style={{ padding: '10px 28px', fontWeight: 700, background: '#2563eb', border: 'none', borderRadius: '8px', boxShadow: '0 4px 6px rgba(37, 99, 235, 0.2)' }}
                  onClick={() => {
                    if (dialog.confirmPayload) {
                      handleCheckHarga(dialog.confirmPayload.kodeTindakan, { key: dialog.confirmPayload.key, value: 'YA' });
                    }
                    setDialog({ ...dialog, show: false });
                  }}
                >
                  Ya, Setuju
                </button>
              </>
            ) : (
              <button 
                className="btn btn-danger" 
                style={{ padding: '10px 32px', fontWeight: 700, border: 'none', borderRadius: '8px', boxShadow: '0 4px 6px rgba(239, 68, 68, 0.2)' }}
                onClick={() => setDialog({ ...dialog, show: false })}
              >
                Tutup
              </button>
            )}
          </div>
        </Modal.Body>
      </Modal>

      <Modal show={showModal} onHide={() => setShowModal(false)} size="xl" centered>
        <div style={{ background: `linear-gradient(135deg,${T.navy},${T.blue})`, padding: '16px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderRadius: '8px 8px 0 0' }}>
          <div style={{ color: '#fff', fontWeight: 700, fontSize: '0.95rem', letterSpacing: '0.8px' }}>
            <i className="bi bi-search me-2" style={{ color: T.sky }}></i>
            PENCARIAN DATA PASIEN {tipePasien ? `- ${tipePasien.label}` : ''}
          </div>
          <button onClick={() => setShowModal(false)} style={{ background: 'transparent', border: 'none', color: 'rgba(255,255,255,0.7)', fontSize: '1.5rem', lineHeight: 1, cursor: 'pointer' }}>&times;</button>
        </div>
        <Modal.Body style={{ background: '#f8fafc', padding: '20px 24px' }}>
          <div style={{ position: 'relative', marginBottom: '16px' }}>
            <i className="bi bi-search" style={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }}></i>
            <FormControl 
              placeholder="Ketik Nama atau No. KIRS untuk mencari..." 
              value={modalSearch}
              onChange={handleModalSearch}
              style={{ paddingLeft: 44, height: 44, borderRadius: 8, fontSize: '0.9rem', border: `1px solid ${T.border}`, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}
              autoFocus
            />
          </div>

          <div style={{ maxHeight: '60vh', overflowY: 'auto', borderRadius: '8px', border: `1px solid ${T.border}`, boxShadow: '0 2px 8px rgba(0,0,0,0.04)', background: '#fff' }}>
            <Table hover responsive className="mb-0" style={{ fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
              <thead style={{ position: 'sticky', top: 0, zIndex: 1, background: T.slate, boxShadow: '0 1px 2px rgba(0,0,0,0.08)' }}>
                <tr>
                  <th className="py-3 px-3" style={{ fontWeight: 700, color: '#475569' }}>KIRS</th>
                  <th className="py-3 px-3" style={{ fontWeight: 700, color: '#475569' }}>NAMA PASIEN</th>
                  <th className="py-3 px-3" style={{ fontWeight: 700, color: '#475569' }}>DOKTER / PENGIRIM</th>
                  <th className="py-3 px-3" style={{ fontWeight: 700, color: '#475569' }}>RUANG / KELAS</th>
                  <th className="py-3 px-3" style={{ fontWeight: 700, color: '#475569' }}>JAMINAN</th>
                  <th className="py-3 px-3 text-center" style={{ fontWeight: 700, color: '#475569', width: 90 }}>AKSI</th>
                </tr>
              </thead>
              <tbody>
                {modalLoading ? (
                  <tr>
                    <td colSpan="6" className="text-center py-5 text-muted">
                      <Spinner animation="border" variant="primary" size="sm" className="me-2" />
                      Memuat data pasien...
                    </td>
                  </tr>
                ) : filteredData.length > 0 ? (
                  filteredData.map((p, idx) => (
                    <tr key={idx} style={{ verticalAlign: 'middle', borderBottom: `1px solid ${T.border}` }}>
                      <td className="px-3 fw-bold text-primary">{p.no_medrec}</td>
                      <td className="px-3 fw-bold text-dark">{p.nama}</td>
                      <td className="px-3 text-secondary">{p.nama_dok || p.nama_dokter || '-'}</td>
                      <td className="px-3 text-secondary">{p.no_kamar || p.ruangRawat || p.nm_ruang || '-'}</td>
                      <td className="px-3">
                        <span style={{ background: '#e0e7ff', color: '#4f46e5', padding: '4px 10px', borderRadius: 4, fontSize: '0.72rem', fontWeight: 700 }}>
                          {p.nm_klasifikasi || p.kd_klasifikasi || p.penjamin || '-'}
                        </span>
                      </td>
                      <td className="px-3 text-center">
                        <button 
                          style={{ background: '#10b981', color: '#fff', border: 'none', borderRadius: 6, padding: '5px 14px', fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer' }}
                          onClick={() => handleSelectPasien(p)}
                        >
                          <i className="bi bi-check2 me-1"></i>Pilih
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="text-center py-5 text-muted">
                      <i className="bi bi-person-x" style={{ fontSize: '2.5rem', opacity: 0.2, display: 'block', marginBottom: 8 }}></i>
                      Tidak ada data pasien ditemukan.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
}
